/**
 * Helpers for reading npt-api error responses.
 *
 * The API reports failures as an array of `{ title, detail, ... }` (sometimes wrapped as
 * `{ errors: [...] }`, sometimes a plain `{ message }`).
 *
 * NPT-2110: error kinds are recognised by status and stable code instead of by matching text.
 * npt-api used to return raw MySQL text for database errors (`400 Duplicate entry '3' for key ...`),
 * and the program form matched on "Duplicate". The API now returns `409` with
 * `errors[0].errorCode === 'DUPLICATE_ENTRY'` and a safe message. Both shapes are accepted here, so
 * web and API can deploy in either order.
 */
export const DUPLICATE_ENTRY = 'DUPLICATE_ENTRY';

export const SINGLE_EVENT_INVARIANT_CODES = {
   SINGLE_EVENT_NO_PARENT: 'SINGLE_EVENT_NO_PARENT',
   SINGLE_EVENT_NO_CHILDREN: 'SINGLE_EVENT_NO_CHILDREN',
   SINGLE_EVENT_ONE_EVENT: 'SINGLE_EVENT_ONE_EVENT',
   SINGLE_EVENT_NO_SCHEDULE: 'SINGLE_EVENT_NO_SCHEDULE',
} as const;

export type SingleEventInvariantCode =
   (typeof SINGLE_EVENT_INVARIANT_CODES)[keyof typeof SINGLE_EVENT_INVARIANT_CODES];

export const SINGLE_EVENT_INVARIANT_I18N_KEYS: Record<SingleEventInvariantCode, string> = {
   [SINGLE_EVENT_INVARIANT_CODES.SINGLE_EVENT_NO_PARENT]:
      'organization.addedit.singleEventInvariants.SINGLE_EVENT_NO_PARENT',
   [SINGLE_EVENT_INVARIANT_CODES.SINGLE_EVENT_NO_CHILDREN]:
      'organization.addedit.singleEventInvariants.SINGLE_EVENT_NO_CHILDREN',
   [SINGLE_EVENT_INVARIANT_CODES.SINGLE_EVENT_ONE_EVENT]:
      'organization.addedit.singleEventInvariants.SINGLE_EVENT_ONE_EVENT',
   [SINGLE_EVENT_INVARIANT_CODES.SINGLE_EVENT_NO_SCHEDULE]:
      'organization.addedit.singleEventInvariants.SINGLE_EVENT_NO_SCHEDULE',
};

export const SINGLE_EVENT_INVARIANT_MESSAGES: Record<SingleEventInvariantCode, string> = {
   [SINGLE_EVENT_INVARIANT_CODES.SINGLE_EVENT_NO_PARENT]:
      'A Single Event organization cannot have a parent organization.',
   [SINGLE_EVENT_INVARIANT_CODES.SINGLE_EVENT_NO_CHILDREN]:
      'A Single Event organization cannot have child organizations (chapters).',
   [SINGLE_EVENT_INVARIANT_CODES.SINGLE_EVENT_ONE_EVENT]:
      'A Single Event organization cannot have more than one active event.',
   [SINGLE_EVENT_INVARIANT_CODES.SINGLE_EVENT_NO_SCHEDULE]:
      'A Single Event organization cannot have a schedule or season plan.',
};

type ApiErrorItem = {
   code?: number;
   title?: string;
   errorCode?: string;
   type?: string;
   detail?: unknown;
   [extra: string]: unknown;
};
type ApiErrorBody = ApiErrorItem[] | { errors?: ApiErrorItem[]; message?: unknown } | null | undefined;

const errorBody = (error: unknown): ApiErrorBody =>
   (error as { response?: { data?: ApiErrorBody } } | null | undefined)?.response?.data;

/** First error item, whether the body is the bare array or wrapped as `{ errors: [...] }`. */
const firstApiError = (error: unknown): ApiErrorItem | undefined => {
   const data = errorBody(error);
   return Array.isArray(data) ? data[0] : data?.errors?.[0];
};

export const getSingleEventInvariantErrorCode = (error: unknown): SingleEventInvariantCode | undefined => {
   const first = firstApiError(error);
   const code = (first?.errorCode || first?.type) as SingleEventInvariantCode | undefined;
   if (code && code in SINGLE_EVENT_INVARIANT_I18N_KEYS) {
      return code;
   }
   return undefined;
};

/**
 * Returns the message to show a user, or undefined when the error carries none (a dropped
 * connection, say), so callers can `getApiErrorMessage(err) || fallback`.
 */
export const getApiErrorMessage = (error: unknown, t?: (key: string) => string): string | undefined => {
   const invCode = getSingleEventInvariantErrorCode(error);
   if (invCode) {
      if (t) {
         const translated = t(SINGLE_EVENT_INVARIANT_I18N_KEYS[invCode]);
         if (translated && translated !== SINGLE_EVENT_INVARIANT_I18N_KEYS[invCode]) {
            return translated;
         }
      }
      return SINGLE_EVENT_INVARIANT_MESSAGES[invCode];
   }
   const data = errorBody(error);
   const message = firstApiError(error)?.detail ?? (Array.isArray(data) ? undefined : data?.message);
   return typeof message === 'string' && message.trim() ? message : undefined;
};

export const isDuplicateEntryError = (err: unknown): boolean => {
   const first = firstApiError(err);
   if (first?.errorCode === DUPLICATE_ENTRY) return true;
   // Older API builds: raw driver text in the detail.
   return typeof first?.detail === 'string' && first.detail.includes('Duplicate');
};

/** 429 from the API (for example the guest RSVP limit). */
export const isRateLimitedError = (err: unknown): boolean =>
   (err as { response?: { status?: number } } | null | undefined)?.response?.status === 429;

/** The stable `errorCode` npt-api attaches to a refusal (e.g. NPT-2060's NON_REFUNDABLE), if any. */
export const getApiErrorCode = (err: unknown): string | undefined => {
   const code = firstApiError(err)?.errorCode;
   return typeof code === 'string' && code ? code : undefined;
};

/** Another field npt-api attached to the first error item (e.g. `changeDeadline`). */
export const getApiErrorField = <T = unknown>(err: unknown, field: string): T | undefined => firstApiError(err)?.[field] as T | undefined;
